import { ELanguage, TTranslator } from '@smartshell/ui-kit';
import dayjs, { Dayjs } from 'dayjs';

import { TLocalesKeys } from 'localization';
import {
  ICashOrder,
  IServicePaymentItem,
  IUser,
  IWorker,
  TPayment,
  TShopItem,
  EDuration,
  ITimeIntervalReturnValue,
} from 'types';

export const NBSP = '\u00a0';

/**
 * Formats seconds to HH:mm:ss
 * @author Gobl1n
 */
export const convertToHours = (
  seconds_left: number,
  secondsDisabled?: boolean
): string => {
  const hours = (seconds_left / 3600) ^ 0;
  const minutes = ((seconds_left - hours * 3600) / 60) ^ 0;
  let seconds = seconds_left - hours * 3600 - minutes * 60;

  if (isNaN(seconds)) {
    seconds = 0;
  }

  seconds = Math.floor(seconds);

  if (secondsDisabled) {
    if (minutes < 5) {
      return (
        (minutes < 10 ? '0' + minutes : minutes) +
        ':' +
        (seconds < 10 ? '0' + seconds : seconds) +
        ''
      );
    } else {
      return (
        (hours < 10 ? '0' + hours : hours) +
        ':' +
        (minutes < 10 ? '0' + minutes : minutes)
      );
    }
  }
  return (
    (hours < 10 ? '0' + hours : hours) +
    ':' +
    (minutes < 10 ? '0' + minutes : minutes) +
    ':' +
    (seconds < 10 ? '0' + seconds : seconds) +
    ''
  );
};

/**
 * Returns User FIO in format {last_name first_name[0].}
 * @param user
 * @return string
 */
export const getUserFIO = (user: Partial<IUser> | null): string => {
  if (!user) {
    return '-';
  }

  return `${user.last_name}${NBSP}${user.first_name?.[0]}.`;
};

/**
 * Squashes same payment items with type = SERVICE
 * @param payments - array of Payments
 * @return Array<IPaymentItem>
 */
export const getSoldServices = (
  payments: Array<TPayment>
): Array<IServicePaymentItem> =>
  Object.values(
    payments
      ?.flatMap((payment) => payment.items)
      .reduce<Record<string, IServicePaymentItem>>((acc, service) => {
        if (service.type !== 'SERVICE') return acc;

        const title = service.entity.title;

        const accumulatedService = acc[title];

        if (accumulatedService) {
          return {
            ...acc,
            [title]: {
              ...service,
              amount: accumulatedService.amount + service.amount,
              sum: accumulatedService.sum + service.sum,
            },
          };
        }

        return {
          ...acc,
          [title]: {
            ...service,
          },
        };
      }, {}) || {}
  );

/**
 * Calls passed fn 1 time in the end of delay.
 * @param fn - function for call after delay
 * @param delay
 */
type Debounced<F extends (...args: any[]) => void> = ((
  ...args: Parameters<F>
) => void) & {
  cancel: () => void;
};

export function debounce<F extends (...args: any[]) => void>(
  fn: F,
  delay: number
): Debounced<F> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<F>): void => {
    if (timer) clearTimeout(timer);

    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };

  debounced.cancel = (): void => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return debounced;
}

/**
 * Calls passed fn 1 time per delay.
 * @param fn - function for call after delay
 * @param delay
 */
export const throttle = <T extends CallableFunction>(
  fn: T,
  delay: number
): T => {
  let lastCallTime: number | null = null;

  // @ts-ignore 123123
  return (...args) => {
    if (!lastCallTime) {
      lastCallTime = Date.now();
    }

    if (Date.now() - lastCallTime > delay) {
      lastCallTime = Date.now();

      return fn(...args);
    }
  };
};

/**
 * Remove controls from storybook
 * @param properties
 */
export const removeStorybookControls = (
  properties: Array<string>
): Record<string, Record<'control', null>> =>
  properties.reduce(
    (acc, property) => ({ ...acc, [property]: { control: null } }),
    {}
  );

/**
 * Convert time periods into seconds
 * @param duration quantity of elements matches type
 * @param type seconds | minutes | hours | days | weeks
 */
export const getSeconds = (
  duration: number | '' | undefined,
  type: string
): number | undefined => {
  if (!duration || !type) {
    return 0;
  }

  switch (type) {
    case 'seconds':
      return duration;
    case 'minutes':
      return duration * 60;
    case 'hours':
      return duration * 60 * 60;
    case 'days':
      return duration * 60 * 60 * 24;
    case 'weeks':
      return duration * 60 * 60 * 24 * 7;

    default:
      throw new Error('Unhandled type of duration');
  }
};

/**
 * Display seconds as the biggest time interval which divides without a reminder
 * if it's not dividable by 60 throws Error
 * @param seconds quantity of elements matches type
 * @param t translator
 * @return value: number, interval: 'minutes' | 'hours' | 'days' | 'weeks'
 */
export const assumeDuration = (
  seconds: number,
  t: TTranslator<TLocalesKeys>
): ITimeIntervalReturnValue | undefined => {
  if (seconds === 1) {
    return;
  }

  if (seconds % (60 * 60 * 24 * 7) === 0) {
    const value = seconds / (60 * 60 * 24 * 7);

    return {
      value,
      interval: EDuration.weeks,
      title: t('time.contracted.weeks', value),
    };
  }

  if (seconds % (60 * 60 * 24) === 0) {
    const value = seconds / (60 * 60 * 24);

    return {
      value,
      interval: EDuration.days,
      title: t('time.contracted.days', value),
    };
  }

  if (seconds % (60 * 60) === 0) {
    const value = seconds / (60 * 60);

    return {
      value,
      interval: EDuration.hours,
      title: t('time.contracted.hours', value),
    };
  }

  if (seconds % 60 === 0) {
    const value = seconds / 60;

    return {
      value,
      interval: EDuration.minutes,
      title: t('time.contracted.minutes', value),
    };
  }

  return {
    title: '',
    value: 0,
    interval: EDuration.minutes,
  };
};

/**
 * Calculates quantity of time periods in given seconds and return biggest
 * @param seconds quantity of seconds
 * @param t translator
 * @return value: number, interval: 'minutes' | 'hours' | 'days' | 'weeks', title: human-readable representation
 */
export const transformTime = (
  seconds: number | undefined,
  t: TTranslator<TLocalesKeys>
): ITimeIntervalReturnValue | undefined => {
  if (!seconds || seconds === 0) {
    return undefined;
  }

  const minute = 60;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;

  const secondsInWeeks = seconds / week;
  const secondsInDays = seconds / day;
  const secondsInHours = seconds / hour;
  const secondsInMinutes = seconds / minute;

  if (secondsInWeeks >= 1) {
    return {
      value: Math.floor(secondsInWeeks),
      interval: EDuration.weeks,
      title: t('time.contracted.weeks', Math.floor(secondsInWeeks)),
    };
  }

  if (secondsInDays >= 1) {
    return {
      value: Math.floor(secondsInDays),
      interval: EDuration.days,
      title: t('time.contracted.days', Math.floor(secondsInDays)),
    };
  }

  if (secondsInHours >= 1) {
    return {
      value: Math.floor(seconds / hour),
      interval: EDuration.hours,
      title: t('time.contracted.hours', Math.floor(secondsInHours)),
    };
  }

  if (secondsInMinutes >= 1) {
    return {
      value: Math.floor(secondsInMinutes),
      interval: EDuration.minutes,
      title: t('time.contracted.minutes', Math.floor(secondsInMinutes)),
    };
  }

  return {
    value: Math.ceil(secondsInMinutes),
    interval: EDuration.minutes,
    title: t('time.contracted.minutes', Math.floor(secondsInMinutes)),
  };
};

interface ISecondsToWeekDaysReturnValue {
  day: number;
  hour: string;
}
/**
 * Calculates what day of week and hour represented by seconds if imagine that monday 00:00 is 0 seconds
 * @param seconds quantity of seconds
 * @return day: {title: day of week, value: number}, hour: 'HH:mm'
 */
export const secondsToWeekDays = (
  seconds: number
): ISecondsToWeekDaysReturnValue => {
  const dayValues: Array<number> = [1, 2, 3, 4, 5, 6, 7];

  const week = 604800;

  const fullHours = Math.floor(seconds / 3600);

  const fullDays = Math.floor(fullHours / 24);

  const restHours = fullHours - fullDays * 24;

  const restMinutes = (seconds - fullHours * 3600) / 60;

  const formattedHours = restHours >= 10 ? restHours : `0${restHours}`;
  const formattedMinutes = restMinutes >= 10 ? restMinutes : `0${restMinutes}`;

  return {
    day: dayValues[seconds >= week ? 0 : fullDays],
    hour: `${formattedHours}:${formattedMinutes}`.slice(0, 5),
  };
};

interface ISchedule {
  start: number;
  end: number;
}

const MINUTE_TIME = 60;
const HOUR_TIME = 3600;
const DAY_TIME = 86400;
const WEEK_TIME = 604800;
/**
 * Calculates how many seconds should pass until specific day and time
 * @return seconds quantity of seconds
 * @param time
 * @param selected_days
 */
export const weekDaysToSeconds = (
  time: {
    start: string;
    end: string;
  },
  selected_days: Array<number>
): Array<ISchedule> => {
  const schedules = [];

  const [startHoursStr, startMinutesStr] = time.start.split(':');
  const [endHoursStr, endMinutesStr] = time.end.split(':');

  const startHours = Number(startHoursStr) * HOUR_TIME;
  const startMinutes = Number(startMinutesStr) * MINUTE_TIME;

  const endHours = Number(endHoursStr) * HOUR_TIME;
  const endMinutes = Number(endMinutesStr) * MINUTE_TIME;

  const startSeconds = startHours + startMinutes + 1;
  let endSeconds = endHours + endMinutes;

  if (endSeconds < startSeconds) {
    endSeconds += DAY_TIME;
  }

  for (const day of selected_days) {
    const allDaySeconds = (day - 1) * DAY_TIME;

    const endTime = allDaySeconds + endSeconds;

    const isEndTimeMoreWeek = endTime > WEEK_TIME;

    schedules.push({
      start: allDaySeconds + startSeconds,
      end: isEndTimeMoreWeek ? endTime - WEEK_TIME : endTime,
    });
  }

  return schedules;
};

/**
 * Checks arrays equality
 * @param arr1
 * @param arr2
 * @returns boolean
 */
export const checkArraysEquality = (
  arr1: Array<unknown>,
  arr2: Array<unknown>
): boolean => {
  if (arr1 === arr2) return true;
  if (arr1 == null || arr2 == null) return false;
  if (arr1.length !== arr2.length) return false;

  return arr1.every((item) => arr2.includes(item));
};

/**
 * Makes an array depends on given range
 * @param first first element of array
 * @param last last element of array
 * @returns array
 */
export const getRange = (first: number, last: number): number[] => {
  const range = [];
  if (first < last) {
    for (let i = first; i <= last; i++) {
      range.push(i);
    }
  } else {
    return [first];
  }
  return range;
};

/**
 * Makes a week day array depends on given range
 * @param first first element of array
 * @param last last element of array
 * @returns array
 */
export const getWeekRange = (first: number, last: number): number[] => {
  const range = [];
  if (first < last) {
    for (let i = first; i <= last; i++) {
      range.push(i);
    }
  } else {
    // eslint-disable-next-line for-direction
    for (let i = first; i >= last; i++) {
      range.push(i);
      if (i >= 7) {
        i = 1;
        break;
      }
    }
    for (let i = 1; i <= last; i++) {
      range.push(i);
    }
  }
  return range;
};

/**
 * check if disabled is fn and call it with passed data
 * otherwise return disabled as boolean value
 *
 * @param data for passing in disabled fn
 * @param {boolean | ((data) => boolean)} disabled
 * @return {boolean}
 */
export function checkIsDisabled<T>(
  data: T,
  disabled?: boolean | ((data: T) => boolean)
): boolean {
  if (typeof disabled === 'function') {
    return disabled(data);
  }

  return disabled ?? false;
}

/**
 * check if objects are equal (not deep)
 * @param obj1 object for equality check
 * @param obj2 object for equality check
 * @return {boolean} boolean - is objects equal
 */
export const shallowEqual = <T>(obj1: T, obj2: T): boolean => {
  if (Object.keys(obj1).length !== Object.keys(obj2).length) {
    return false;
  }

  for (const key in obj1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
};

/**
 * return random 6-digits numeric string
 *
 * @return {string} random 6-digits number as string
 */
export const getRandomNumber = (): string =>
  (Math.random() * 1000000).toFixed();

/**
 * returns amount limit for shop item
 * @param {IBaseShopItem} item
 * @return {number}
 */
export const calcLimit = (item: TShopItem): number => {
  const limitByPrice = Math.floor(16000000 / item.cost);

  if (limitByPrice < 100) {
    return limitByPrice;
  }

  if (item.type === 'GOOD' && item.original.amount < 99) {
    return item.original.amount;
  }

  return 99;
};

/**
 * calculates item total price using amount, cost and discount
 * @param { use_global_discounts: boolean; amount: number; cost: number } item shop item
 * @param {number | null} discount discount if exists
 * @return {number} item total price
 */
export const getItemTotalPrice = (
  item: { use_global_discounts: boolean; amount: number; cost: number },
  discount?: number | null
): number => {
  const discountValue = item.use_global_discounts && discount ? discount : 0;

  return Math.round(item.amount * item.cost * (100 - discountValue)) / 100;
};

/**
 * calculates total shop price
 * @param items - shop cart items
 * @param discount - discount
 * @param hostIds - selected hosts ids
 */
export const getShoppingCartTotalPrice = (
  items: Array<TShopItem>,
  discount: number | null,
  availableItems?: Array<{ entity_id: number; entity_type: string }> | null,
  hostIds?: number[]
): number => {
  if (!availableItems?.length) {
    if (hostIds && hostIds.length > 1) {
      return items.reduce(
        (acc, item) => acc + getItemTotalPrice(item, discount) * hostIds.length,
        0
      );
    }
    return items.reduce(
      (acc, item) => acc + getItemTotalPrice(item, discount),
      0
    );
  }

  const isItemApplicableForDiscount = (item: TShopItem): boolean => {
    if (!discount) return false;

    return availableItems.some(
      (availableItem) =>
        availableItem.entity_id === item.id &&
        availableItem.entity_type === item.type
    );
  };

  const getItemPriceWithDiscount = (item: TShopItem): number => {
    if (item.comboId) {
      return getItemTotalPrice(item, discount);
    }

    const shouldApplyDiscount = isItemApplicableForDiscount(item);
    const discountToApply = shouldApplyDiscount ? discount : null;
    return getItemTotalPrice(item, discountToApply);
  };

  if (hostIds && hostIds.length > 1) {
    return items.reduce(
      (acc, item) => acc + getItemPriceWithDiscount(item) * hostIds.length,
      0
    );
  }

  return items.reduce((acc, item) => acc + getItemPriceWithDiscount(item), 0);
};
export const getItemTotalPriceForReceipt = (
  item: TShopItem,
  discount?: number,
  availableItems?: Array<{ entity_id: number; entity_type: string }> | null
): number => {
  if (!availableItems?.length) {
    return getItemTotalPrice(item, discount);
  }
  const isApplicable = availableItems.some(
    (av) => av.entity_id === item.id && av.entity_type === item.type
  );

  const itemDiscount = isApplicable && discount ? discount : 0;

  return Math.round(item.amount * item.cost * (100 - itemDiscount)) / 100;
};
/**
 * Преобразует время указанное в тарифе в объект DayJS с текущей датой
 * @param time время в формате "hh:mm:ss"
 */
export const formatTariffTime = (time: string): Dayjs => {
  const [hours, minutes, seconds] = time.split(':').map((time) => Number(time));

  return dayjs()
    .set('hours', hours)
    .set('minutes', minutes)
    .set('seconds', seconds);
};

/**
 * Рассчитывает длительность всех тарифов, добавленных в корзину.
 * В расчетах есть 3 варианта, исходя из того, что мы мутируем сегодняшнюю дату, выставляя на ней время равное времени окончание тарифа:
 * (пример, окончание: 23:59:59, сейчас 15.02.2022 8:30:34 - получаем, что время окончания = 15.02.2022 23:59:59
 * 1) Тариф не пакетный (просто добавляем его duration)
 * 2) Тариф пакетный и НЕ ночной (добавляем разницу между уже рассчитанным временем и временем окончания тарифа)
 * 3) Тариф пакетный и ночной (добавляем разницу между уже рассчитанным временем и временем окончания тарифа + 1 день)
 * @param items айтемы из корзины
 * @param currentSessionEndTime время окончания текущей сессии
 */
export const getTariffsDuration = (
  items: Array<TShopItem>,
  currentSessionEndTime?: string | null
): number =>
  items.reduce(
    (acc, item) => {
      if (item.type !== 'TARIFF') {
        return acc;
      }

      if (item.original.has_fixed_finish_time && !item.original.per_minute) {
        const endTime = formatTariffTime(item.original.time_to);

        const now = dayjs().add(acc, 'seconds');

        if (endTime.isBefore(now)) {
          return acc + endTime.add(1, 'day').diff(now, 'seconds');
        }

        return acc + endTime.diff(now, 'seconds');
      }

      return acc + item.original.duration * item.amount;
    },
    currentSessionEndTime
      ? dayjs(currentSessionEndTime).diff(dayjs(), 'seconds')
      : 0
  );

/**
 * Принимает массив кассовых ордеров и возвращает их сумму с учетом типа
 * @param cashOrders массив кассовых ордеров
 */
export const getCashOrdersDiff = (
  cashOrders: Array<Pick<ICashOrder, 'type' | 'sum'>>
): number =>
  cashOrders.reduce((acc, order) => {
    if (order.type === 'PKO') {
      return acc + order.sum;
    }

    if (order.type === 'RKO') {
      return acc - order.sum;
    }

    return acc;
  }, 0);

/**
 * Форматирует пользователя в формат '{Фамилия} {Первая буква имени}.'
 *
 * @param Worker {last_name, first_name}
 * @return string
 */
export const formatFIO = ({
  last_name,
  first_name,
}: Partial<IWorker>): string =>
  `${last_name} ${first_name && first_name[0] + '.'}`;

/**
 * Форматирует телефон в формат +7 (999) 999-99-99
 *
 * @param p {string} телефон
 * @return string
 */
export const formatPhone = (p: string): string =>
  p.length !== 11
    ? p
    : `+${p[0]} (${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(
        9,
        11
      )}`;

export const formatSum = (
  sum: number | undefined,
  currency: string,
  options?: Intl.NumberFormatOptions
): string =>
  `${sum?.toLocaleString(undefined, options) ?? 0} ${
    currency === 'empty' ? '' : currency
  }`;

export const getLastClientActivityText = (
  lastActivity: string | null | undefined,
  t: TTranslator<TLocalesKeys>
): string => {
  if (!lastActivity) {
    return t('client.visit.no');
  }

  const date = dayjs(lastActivity);
  const diffFromNow = dayjs().diff(date, 'days');

  if (diffFromNow === 0) {
    return t('client.visit.today');
  }

  if (diffFromNow === 1) {
    return t('client.visit.yesterday');
  }

  return t('client.visit.was', date.format('DD.MM.YYYY'));
};

export const formatDateForServer = (date: Dayjs): string =>
  date.format(`YYYY-MM-DD HH:mm:ss`);

export const formatNumber = (num: number): string =>
  num.toLocaleString(undefined, { maximumFractionDigits: 2 });

// формат даты (до 25 мая 2026 / 25 мая 2026)
export const formatExpirationDate = (
  dateString: string | null | undefined,
  lang: ELanguage,
  prefix?: string
): string => {
  if (!dateString) return '';

  dayjs.locale(lang);

  const date = dayjs(dateString);
  if (!date.isValid()) return '';

  if (prefix) return `${prefix} ${date.format('D MMMM YYYY')}`;
  return `${date.format('D MMMM YYYY')}`;
};

// форматирует число в формат просмотров (1.2 тыс; 1.4млн)
export const formatCompactNumber = (
  value: number,
  t: TTranslator<TLocalesKeys>
): string => {
  const removeZero = (value: number): string =>
    value.toFixed(1).replace('.', ',').replace(',0', '');

  if (value < 1000) {
    return String(value);
  }

  if (value < 1_000_000) {
    const compact = Math.floor(value / 100) / 10;

    return `${removeZero(compact)} ${t('thousand')}`;
  }

  const compact = Math.floor(value / 100_000) / 10;

  return `${removeZero(compact)} ${t('million')}`;
};
